/* =====================================================================
   freshaHistoryService — reads the authoritative Fresha-era per-barber
   MONTHLY financials (Firestore collection `freshaHistory`, imported from the
   Fresha sales-log + tips-detail via the admin service account, 2026-09-08).
   Service revenue INCLUDES add-ons (line-item source, no multi-service collapse);
   tips are exact monthly. This is the barber-financial system-of-record until the
   SLIK cutover — the reports read it for the Fresha era and blend live SLIK data
   for months after the cutover.
   See memory: project-slik-backlog-parked-items-2026-09-08 (items 6 & 7),
   project-slik-firestore-admin-and-reporting-reconciliation-2026-08-31.
===================================================================== */
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getOperatorDb, getOperatorBarberId, operatorSeesAll, onOperatorChange } from './operatorSession';

export interface FreshaMonth {
  svcNet: number; prodNet: number; tips: number;
  tickets?: number; svcLines?: number; addonLines?: number;
}
export interface FreshaYearTotal { svcNet: number; prodNet: number; tips: number; tickets?: number; }
export interface FreshaBarber {
  id: string;
  barberId: string | null;
  barberName: string;
  freshaName: string;
  matched: boolean;
  days: Record<string, FreshaMonth>;              // 'YYYY-MM-DD' → day figures (exact-range source)
  months: Record<string, FreshaMonth>;            // 'YYYY-MM' → month figures (charts)
  totals: Record<string, FreshaYearTotal>;        // 'YYYY'    → year figures
}

let _cache: FreshaBarber[] | null = null;
// SECURITY (Phase B 2026-09-30): financials are read ONLY through the tapped-in OPERATOR session, so
// clear the cache whenever the operator changes (owner sees all / barber sees own / nobody sees none).
// ⚡ PERF (Jose 2026-09-30): when an OWNER/MANAGER taps in, PREFETCH in the background so the ~180KB
// load happens WHILE they navigate from the PIN pad → the report opens from cache (no visible wait).
onOperatorChange(() => {
  _cache = null;
  if (operatorSeesAll()) { loadFreshaHistory().catch(() => {}); }
});

/** Drop the in-memory cache so the next loadFreshaHistory() re-reads Firestore (call after an import). */
export function clearFreshaHistoryCache(): void { _cache = null; }

const mapFresha = (id: string, x: any): FreshaBarber => ({
  id,
  barberId: x.barberId ?? null,
  barberName: x.barberName || x.freshaName || 'Unknown',
  freshaName: x.freshaName || x.barberName || '',
  matched: !!x.matched,
  days: x.days || {},
  months: x.months || {},
  totals: x.totals || {},
});

// ⚡ PERF NOTE (Jose 2026-09-30, "major Fresha delays on the iPads"): this fetches EVERY freshaHistory
// doc IN FULL on each cold call — ~180 KB total on prod, and ~90% of that is the per-DAY `days` map
// (Jose alone = 73 KB / 908 daily entries). Most reports only chart the tiny `months` rollup (34 pts).
// Over the iPad's long-polling connection that 180 KB is the bulk of the wait, and `_cache` is cleared
// on every operator change (security) so it re-downloads on each tap-in. THE FIX (not yet done, needs a
// data migration + Jose's OK): split `days` into a separate `freshaHistoryDays/{id}` collection loaded
// ON DEMAND (only when a Week/Custom date-range card needs it) → the default report load drops ~10×.
// Measured cost: cold read ~540ms (fast net) / seconds on iPad; operator-session setup only +~176ms.
export async function loadFreshaHistory(force = false): Promise<FreshaBarber[]> {
  if (_cache && !force) return _cache;
  // Read through the OPERATOR session if tapped in, ELSE the main login (an owner/manager logged in
  // directly can read all; the bare shop account is still rule-denied → empty). This fixes the case
  // where an owner is logged in directly with no PIN tapped. Try the whole collection (works for
  // owner/manager/admin); on denial the reader is a barber → read only their own doc.
  const rdb = getOperatorDb() || db;
  try {
    const snap = await getDocs(collection(rdb, 'freshaHistory'));
    _cache = snap.docs.map((d) => mapFresha(d.id, d.data()));
    return _cache;
  } catch (e) {
    // H17 FIX (Jose 2026-10-29 audit): a THROWN read error is NOT a genuine empty. The old code cached
    // `[]` here, so a single transient permission/network blip POISONED the session cache — every later
    // payroll/report then silently ran Fresha-less, and any pay window spanning the cutover UNDERPAID the
    // barber (pre-cutover reconciled revenue vanished with zero trace). Now: log it, still try the barber's
    // OWN doc (legit scoped read), but DO NOT cache an error-empty — leave _cache null so the next call
    // retries and a momentary blip can't silently zero someone's pay for the whole session.
    console.warn('[freshaHistory] collection read failed (will retry on next call, NOT cached):', (e as any)?.message || e);
    const bid = getOperatorBarberId();
    if (bid) {
      try {
        const d = await getDoc(doc(rdb, 'freshaHistory', bid));
        if (d.exists()) { _cache = [mapFresha(d.id, d.data())]; return _cache; }
      } catch { /* own-doc denied too */ }
    }
    return []; // transient empty — intentionally NOT written to _cache
  }
}

// ---- month helpers (all keys are 'YYYY-MM', lexicographically comparable) ----
export const ymOf = (isoDate: string): string => (isoDate || '').slice(0, 7);

export function shiftYM(ym: string, deltaMonths: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m - 1) + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthsBetween(fromYM: string, toYM: string): string[] {
  const out: string[] = [];
  let cur = fromYM;
  for (let i = 0; i < 600 && cur <= toYM; i++) { out.push(cur); cur = shiftYM(cur, 1); }
  return out;
}
export const countMonths = (fromYM: string, toYM: string): number => monthsBetween(fromYM, toYM).length;

// ---- exact-date-range (daily) helpers — used by the Financial cards so Week/Custom periods
// match payroll to the day (the monthly `months` map is kept only for the trend charts). ----
const fmtISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const shiftISO = (iso: string, deltaDays: number): string => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + deltaDays); return fmtISO(d); };
export const shiftYearISO = (iso: string, years: number): string => { const d = new Date(iso + 'T00:00:00'); d.setFullYear(d.getFullYear() + years); return fmtISO(d); };
export const daysBetweenISO = (a: string, b: string): number => Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000) + 1;
export const todayISO = (): string => fmtISO(new Date());

export interface RangeSum { svcNet: number; prodNet: number; tips: number; tickets: number; }

/** Sum the exact [fromISO, toISO] range from the per-day map (inclusive). YYYY-MM-DD strings sort. */
export function sumDaysRange(b: FreshaBarber, fromISO: string, toISO: string): RangeSum {
  const acc: RangeSum = { svcNet: 0, prodNet: 0, tips: 0, tickets: 0 };
  const days = b.days || {};
  for (const iso of Object.keys(days)) {
    if (iso >= fromISO && iso <= toISO) {
      const c = days[iso];
      acc.svcNet += c.svcNet || 0; acc.prodNet += c.prodNet || 0;
      acc.tips += c.tips || 0; acc.tickets += (c.tickets || 0);
    }
  }
  return acc;
}

export function sumRange(b: FreshaBarber, fromYM: string, toYM: string): RangeSum {
  const acc: RangeSum = { svcNet: 0, prodNet: 0, tips: 0, tickets: 0 };
  for (const ym of monthsBetween(fromYM, toYM)) {
    const c = b.months[ym]; if (!c) continue;
    acc.svcNet += c.svcNet || 0; acc.prodNet += c.prodNet || 0;
    acc.tips += c.tips || 0; acc.tickets += c.tickets || 0;
  }
  return acc;
}

/** Latest 'YYYY-MM' present across all barbers = the Fresha data horizon (cutover falls here). */
export function latestMonth(barbers: FreshaBarber[]): string {
  let mx = '';
  for (const b of barbers) for (const ym of Object.keys(b.months)) if (ym > mx) mx = ym;
  return mx;
}
/** Earliest 'YYYY-MM' with real data (skips the tiny Dec-2023 straggler when a floor is given). */
export function earliestMonth(barbers: FreshaBarber[], floor = '2024-01'): string {
  let mn = '';
  for (const b of barbers) for (const ym of Object.keys(b.months)) {
    if (ym < floor) continue;
    if (!mn || ym < mn) mn = ym;
  }
  return mn || floor;
}
